import type { Logger } from "@expert/logging";
import type { Location, Size } from "@expert/shared-types";

import { ActionIcon, Card, CloseButton, Dialog, Flex, Title } from "@mantine/core";
import { useDebouncedCallback, useElementSize } from "@mantine/hooks";
import { useEffect } from "react";
import Draggable from "react-draggable";

import { DragIcon } from "../assets";
import { useDragStore } from "../store";
import classes from "./DraggableComponent.module.css";

interface DraggableComponentProps {
    backgroundColor: string;
    children: React.ReactNode;
    iconColor: string;
    location: Location;
    logger: Logger;
    onClose: () => void;
    opened: boolean;
    setLocation: (location: Location, logger: Logger) => void;
    setSize: (size: Size) => void;
    size: Size;
    testId: string;
    title: string;
}

export function DraggableComponent({
    backgroundColor,
    children,
    iconColor,
    location,
    logger,
    opened,
    size,
    testId,
    title,
    onClose,
    setLocation,
    setSize,
}: DraggableComponentProps) {
    const root = document.documentElement;
    const passiveComponentZIndex = Number(getComputedStyle(root).getPropertyValue("--mantine-priority-highest").trim());
    const activeComponentZIndex = passiveComponentZIndex + 1;

    const { ref: componentRef, width: componentWidth, height: componentHeight } = useElementSize();
    const storeUpdatedSize = useDebouncedCallback(() => {
        if (
            (componentHeight === size.height && componentWidth === size.width) ||
            (componentHeight === 0 && componentWidth === 0) // at initial render, width and height are 0 so we want to use the defaults in the store
        ) {
            return;
        }
        setSize({ height: componentHeight, width: componentWidth });
    }, 500);

    // preserving size when resized
    useEffect(() => {
        storeUpdatedSize();
    }, [componentHeight, componentWidth, storeUpdatedSize]);

    const { draggableComponents, setActiveDraggableComponent } = useDragStore();

    const isComponentInFront = draggableComponents.find((component) => component.title === title)?.isInFront ?? false;

    return (
        <Dialog
            bg={backgroundColor}
            data-testid={testId}
            h={size.height}
            onClose={onClose}
            opened={opened}
            mah="100vh"
            p="md"
            position={{ top: 0, left: 0 }}
            w={size.width}
            withCloseButton
            renderRoot={(props) => (
                <Draggable
                    bounds="body"
                    defaultClassName={classes.draggablePositioning}
                    defaultPosition={{ x: location.x, y: location.y }}
                    handle=".handle"
                    nodeRef={componentRef as React.RefObject<HTMLElement>}
                    onMouseDown={() => {
                        if (!isComponentInFront) setActiveDraggableComponent(title);
                    }}
                    onStop={(_e, data) => {
                        setLocation({ x: data.x, y: data.y }, logger);
                    }}
                >
                    <span ref={componentRef as React.RefObject<HTMLElement>}>
                        <Card {...props} className={classes.container} display="flex" p="md">
                            <Flex
                                align="center"
                                className={`${classes.header} handle`}
                                direction="row"
                                justify="flex-end"
                                mb="md"
                                style={{ cursor: "pointer" }}
                            >
                                <Title className={classes.title} c={iconColor} fw={400}>
                                    {title}
                                </Title>
                                <ActionIcon
                                    data-testid={`${testId}-drag-icon`}
                                    h="1.5rem"
                                    ml="auto"
                                    p=".125rem .19rem"
                                    variant="transparent"
                                >
                                    <DragIcon fill={iconColor} />
                                </ActionIcon>
                                <CloseButton c={iconColor} variant="transparent" ml="md" onClick={onClose} />
                            </Flex>
                            {children}
                        </Card>
                    </span>
                </Draggable>
            )}
            portalProps={{ style: { pointerEvents: "none" } }}
            style={{ pointerEvents: "auto" }}
            zIndex={isComponentInFront ? activeComponentZIndex : passiveComponentZIndex}
        ></Dialog>
    );
}
